<template>
  <div
    class="base-dropdown-wrapper"
    :class="baseClasses"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div
      ref="controlRef"
      class="base-dropdown-wrapper__control"
      @click.stop="handleControlClick"
      @touchend.stop="handleControlTouchEnd"
    >
      <slot name="control" v-bind="{ handleClose, isOpen }" />
    </div>
    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="base-dropdown-wrapper__dropdown"
      :style="dropdownStyles"
    >
      <div v-if="props.withArrow" ref="arrowRef" class="base-dropdown-wrapper__arrow">
        <span
          :style="{
            borderColor: `transparent transparent transparent ${props.bgColor}`,
          }"
        />
      </div>
      <div
        class="base-dropdown-wrapper__content"
        :style="{ backgroundColor: props.bgColor }"
      >
        <div class="base-dropdown-wrapper__inner" :style="{ maxHeight: props.maxHeight }">
          <slot name="dropdown" v-bind="{ ...actions }" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Placement } from '@floating-ui/vue'
import type { CSSProperties } from 'vue'
import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue'

interface Props {
  maxHeight?: string
  bgColor?: string
  width?: number | 'fill' | 'huge' | 'reference'
  referenceElement?: HTMLElement | null
  withFull?: boolean
  withArrow?: boolean
  withRounded?: boolean
  withOverflow?: boolean
  withSurface?: boolean
  placement?: Placement
  offsetY?: number
  offsetX?: number
  off?: boolean
  trigger?: 'click' | 'hover'
  withShadow?: boolean
  strategy?: 'absolute' | 'fixed'
  closeOnControlClick?: boolean
  flipFallbackPlacements?: Placement[]
  shiftPadding?: number
}

const props = withDefaults(defineProps<Props>(), {
  maxHeight: '200px',
  width: 'huge',
  placement: 'bottom',
  bgColor: 'var(--color-surface-bg)',
  offsetY: 8,
  trigger: 'click',
  withOverflow: true,
  withSurface: true,
  withShadow: true,
  strategy: 'absolute',
  closeOnControlClick: true,
  shiftPadding: 8,
})

const controlRef = ref<HTMLElement>()
const dropdownRef = ref<HTMLElement>()
const arrowRef = ref<HTMLElement>()
const isOpen = ref(false)
const controlWidth = ref(0)
const referenceWidth = ref(0)
const wasTouchEvent = ref(false)

const floatingReferenceRef = shallowRef<HTMLElement | null>(null)

function getReferenceElement() {
  if (props.referenceElement instanceof HTMLElement) {
    return props.referenceElement
  }

  return controlRef.value ?? null
}

function syncFloatingReference() {
  floatingReferenceRef.value = getReferenceElement()
}

function updateReferenceMetrics() {
  syncFloatingReference()
  referenceWidth.value = floatingReferenceRef.value?.getBoundingClientRect().width || 0
  controlWidth.value = controlRef.value?.offsetWidth || 0
}

const { floatingStyles, update, placement, y } = useFloating(floatingReferenceRef, dropdownRef, {
  strategy: toRef(props, 'strategy'),
  placement: toRef(props, 'placement'),
  middleware: computed(() => [
    ...(props.withRounded
      ? [offset({ mainAxis: props.offsetY, crossAxis: props.offsetX })]
      : []),
    flip(
      props.flipFallbackPlacements?.length
        ? { fallbackPlacements: props.flipFallbackPlacements }
        : undefined,
    ),
    shift({ padding: props.shiftPadding }),
    ...(props.withArrow ? [arrow({ element: arrowRef })] : []),
  ]),
  whileElementsMounted: autoUpdate,
})

const placementIsTop = computed(() => placement.value.includes('top'))
const placementIsBottom = computed(() => placement.value.includes('bottom'))
const baseClasses = computed(() => ({
  'base-dropdown-wrapper--with-full': props.withFull,
  'base-dropdown-wrapper--with-arrow': props.withArrow,
  'base-dropdown-wrapper--with-rounded': props.withRounded,
  'base-dropdown-wrapper--with-overflow': props.withOverflow,
  'base-dropdown-wrapper--with-surface': props.withSurface,
  'base-dropdown-wrapper--with-shadow': props.withShadow,
  'base-dropdown-wrapper--placement-top': placementIsTop.value && isOpen.value,
  'base-dropdown-wrapper--placement-bottom': placementIsBottom.value && isOpen.value,
}))
const dropdownStyles = computed(() => ({
  ...floatingStyles.value,
  width: props.width === 'reference'
    ? `${referenceWidth.value}px`
    : props.width === 'huge'
      ? `${controlWidth.value}px`
      : props.width === 'fill'
        ? 'auto'
        : `${props.width}px`,
  ...(props.withArrow
    ? {
        transform: `translate(${controlWidth.value / 2}px, calc(${y.value}px - 12px))`,
      }
    : {}),
}))

const gapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
}

const actions = {
  handleClose,
  handleOpen,
  handleToggle,
  gapStyle,
}

function handleClose() {
  isOpen.value = false
}

function handleOpen() {
  if (props.off) {
    return
  }

  isOpen.value = true
}

function handleToggle() {
  if (props.off) {
    return
  }

  isOpen.value = !isOpen.value
}

function handleControlClick(event: MouseEvent) {
  if (props.trigger !== 'click') {
    return
  }

  if (wasTouchEvent.value) {
    wasTouchEvent.value = false
    event.preventDefault()
    event.stopPropagation()
    return
  }

  if (isOpen.value) {
    if (props.closeOnControlClick) {
      handleClose()
    }
    return
  }

  handleOpen()
}

function handleControlTouchEnd(event: TouchEvent) {
  if (props.trigger !== 'click') {
    return
  }

  wasTouchEvent.value = true

  if (isOpen.value) {
    if (props.closeOnControlClick) {
      handleClose()
    }
    setTimeout(() => {
      wasTouchEvent.value = false
    }, 300)
    return
  }

  handleOpen()

  event.preventDefault()

  setTimeout(() => {
    wasTouchEvent.value = false
  }, 300)
}

function handleMouseEnter() {
  if (props.trigger === 'hover' && !props.off) {
    handleOpen()
  }
}

function handleMouseLeave() {
  if (props.trigger === 'hover' && !props.off) {
    handleClose()
  }
}

function handleOutsideClick(event: MouseEvent) {
  if (props.trigger !== 'click' || !isOpen.value) {
    return
  }

  if (wasTouchEvent.value) {
    return
  }

  const target = event.target as HTMLElement
  const reference = getReferenceElement()

  if (
    controlRef.value?.contains(target)
    || dropdownRef.value?.contains(target)
    || reference?.contains(target)
  ) {
    return
  }

  handleClose()
}

onMounted(() => {
  syncFloatingReference()
  document.addEventListener('click', handleOutsideClick)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick)
})

watch(isOpen, async (value) => {
  if (value) {
    await nextTick()
    updateReferenceMetrics()
    update()
  }
})

watch(
  () => props.referenceElement,
  async () => {
    if (!isOpen.value) {
      return
    }

    await nextTick()
    updateReferenceMetrics()
    update()
  },
)

defineExpose({
  isOpen,
  handleOpen,
  handleClose,
  handleToggle,
})
</script>

<style lang="scss" scoped>
.base-dropdown-wrapper {
  display: inline-block;

  &__control {
    display: flex;
    width: 100%;

    &:deep(> *) {
      transform: translate(0, 0) !important;
    }
  }

  &__dropdown {
    z-index: 99999;
  }

  &__inner {
    height: auto;
    transform: scale(1);
    transition: all 0.25s ease;
    position: relative;
    scroll-behavior: smooth;
  }

  &--with-overflow {
    .base-dropdown-wrapper__inner {
      overflow: hidden auto;
    }
  }

  &--with-surface {
    .base-dropdown-wrapper__content {
      padding: rem(10px);
      border-radius: rem(12px);
    }
  }

  &__arrow {
    width: 24px;
    height: 24px;
  }

  &__arrow span {
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: rem(24px) 0 0 rem(24px);
    transform: translateY(12px);
  }

  &--with-rounded &__dropdown &__content {
    border-radius: rem(12px);
  }

  &--placement-top.base-dropdown-wrapper--with-shadow {
    .base-dropdown-wrapper__content {
      box-shadow: 0 -10px 20px -5px rgb(0 0 0 / 5%);
    }

    &:not(.base-dropdown-wrapper--with-arrow, .base-dropdown-wrapper--with-rounded) {
      .base-dropdown-wrapper__content {
        border-radius: rem(12px) rem(12px) 0 0;
      }

      .base-dropdown-wrapper__control:deep(> *) {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
  }

  &--placement-bottom.base-dropdown-wrapper--with-shadow {
    .base-dropdown-wrapper__dropdown .base-dropdown-wrapper__content {
      box-shadow: 0 10px 20px -5px rgb(0 0 0 / 5%);
    }

    &:not(.base-dropdown-wrapper--with-arrow, .base-dropdown-wrapper--with-rounded) {
      .base-dropdown-wrapper__content {
        border-radius: 0 0 rem(12px) rem(12px);
      }

      .base-dropdown-wrapper__control:deep(.base-input__container) {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }

  &--with-full {
    width: 100%;
  }
}
</style>
